import styled from '@emotion/styled'
import { motion } from 'framer-motion'
import { breakpoint } from '../../../../__styling/style-functions/breakpoint'
import { calcRem } from '../../../../__styling/style-functions/calc-rem'
interface JabTrackerBase {
    type?: 'small' | 'large'
}
interface StyledJabTrackerProps extends JabTrackerBase {
    containerRatios: {
        desktop: string
        mobile: string
        tablet: string
        fallbackWidth: number
    }
    isLoading?: boolean
}

interface DifferenceInCasesProps {
    direction: 'up' | 'down'
}

interface StyledDosedRowProps extends JabTrackerBase {}
interface StyledVaxBarProps extends JabTrackerBase {}
interface StyledTitleWrapProps extends JabTrackerBase {}
interface StyledFlexTableProps extends JabTrackerBase {}

interface DotIconProps {
    color: 'black' | 'blue' | 'white'
}
interface StyledGridTitleItemProps extends JabTrackerBase {}

interface StyledGridItemProps extends JabTrackerBase {
    smallPadding?: boolean
}

const red = '#ae0007'
const green = '#0f7e37'
const black = '#2B2D2E'
const blue = '#043DE2'

export const StyledJabTrackerContainer = styled(
    'section',
)<StyledJabTrackerProps>(({ type, containerRatios, isLoading, theme }) => ({
    position: 'relative',
    borderTop: `4px double ${black}`,
    borderBottom: `4px double ${black}`,
    display: type === 'small' ? 'grid' : 'none',
    flexDirection: 'column',
    height: 'auto',
    gridTemplateColumns: '1fr',
    gridTemplateRows: 'repeat(3, auto)',
    maxWidth: type === 'small' ? '100%' : 'unset',
    margin: calcRem(theme.margins.lg, 0),
    [breakpoint('sm')]: {
        display: type === 'large' ? 'grid' : 'none',
        gridTemplateColumns: type === 'large' ? ` ${calcRem(128)} 1fr` : '1fr',
        gridTemplateRows:
            type === 'large' && isLoading
                ? `${calcRem(120)} auto`
                : type === 'large'
                ? 'auto auto'
                : type === 'small' && isLoading
                ? 'repeat(3, 1fr)'
                : 'repeat(3, auto)',
        gridColumnGap: type === 'large' ? ` ${calcRem(15)}` : '0',
    },
    [breakpoint('md')]: {
        margin: calcRem(30, 0),
        display:
            containerRatios.fallbackWidth > 900 && type === 'large'
                ? 'grid'
                : containerRatios.fallbackWidth < 900 && type === 'large'
                ? 'none'
                : containerRatios.fallbackWidth > 900 && type === 'small'
                ? 'none'
                : containerRatios.fallbackWidth < 900 && type === 'small'
                ? 'grid'
                : 'none',
    },
}))

export const StyledGridItem = styled('div')<StyledGridItemProps>(
    ({ smallPadding, type }) => ({
        paddingTop: type === 'large' ? calcRem(16) : '0',
        paddingBottom: smallPadding ? calcRem(8) : calcRem(24),

        ['&:nth-child(2)']: {
            marginBottom: type === 'large' ? '0' : calcRem(8),
            paddingBottom: 0,
        },
        ['&:nth-child(3)']: {
            paddingBottom: 0,
        },
    }),
)
export const StyledTitle = styled('h3')({
    margin: 0,
})

export const StyledGridTitleItem = styled('div')<StyledGridTitleItemProps>(
    ({ type }) => ({
        gridRow: 'span 2',
        paddingTop: type === 'large' ? calcRem(16) : '0',
        paddingBottom: 0,
        display: 'flex',
        flexDirection: type === 'large' ? 'column' : 'column-reverse',
        [`${StyledTitle}`]: {
            marginBottom: type === 'large' ? calcRem(16) : '0',
        },
        ['.jabtracker-image']: {
            width: type === 'large' ? 'auto' : '100%',
            height: type === 'large' ? '64px' : '56px',
            marginBottom: calcRem(8),
            borderBottom: type === 'large' ? 'none' : '1px solid #333',
            objectFit: 'cover',
        },
    }),
)

export const StyledTitleWrap = styled('div')<StyledTitleWrapProps>(
    ({ type }) => ({
        marginBottom: calcRem(16),
        ['p']: {
            margin: 0,
            fontSize: type === 'large' ? calcRem(17) : calcRem(12),
        },
        [`${StyledTitle}`]: {
            fontSize: type === 'large' ? calcRem(22) : calcRem(15),
        },
    }),
)

export const StyledFlexTable = styled('div')<StyledFlexTableProps>(
    ({ type }) => ({
        display: 'flex',
        justifyContent: 'flex-start',
        alignItems: type === 'large' ? 'center' : 'start',
        flexFlow: type === 'large' ? 'unset' : 'column wrap',
        maxHeight: type === 'large' ? 'unset' : calcRem(150),
        flexWrap: 'wrap',

        [`${StyledTableCell}`]: {
            marginBottom: type === 'large' ? '0' : calcRem(8),
            justifyContent: type === 'large' ? 'center' : 'flex-start',
        },
    }),
)

export const StyledTableCell = styled('div')({
    display: 'flex',
    alignItems: 'center',
    marginRight: calcRem(10),
})

export const DifferenceInCases = styled('span')<DifferenceInCasesProps>(
    ({ direction }) => ({
        color: direction === 'up' ? red : green,
    }),
)

export const StateLabel = styled('div')({
    fontWeight: 'bold',
    marginRight: calcRem(8),
})

export const StyledVaxBar = styled('div')<StyledVaxBarProps>(({ type }) => ({
    height: type === 'large' ? '40px' : '22px',
    width: '100%',
    display: 'flex',
    border: `1px solid ${black}`,
    borderRadius: '3px',
    marginBottom: type === 'large' ? calcRem(25) : calcRem(16),
    fontSize: type === 'large' ? calcRem(22) : calcRem(12),

    [`${StyledVaxBarIndicator}`]: {
        minWidth: type === 'large' ? '60px' : '30px',
        paddingLeft: type === 'large' ? calcRem(18) : calcRem(10),
    },
}))
interface StyledVaxBarIndicatorProps {
    percent?: number
}
export const StyledVaxBarIndicator = styled(
    motion.div,
)<StyledVaxBarIndicatorProps>(({ theme }) => ({
    height: '100%',
    width: `0%`,
    display: 'flex',
    alignItems: 'center',

    fontWeight: 'bold',
    color: theme.colors.actions.button.primary.text.default,
    ['&:nth-child(1)']: {
        background: black,
    },
    ['&:nth-child(2)']: {
        background: blue,
    },
    ['&:nth-child(3)']: {
        color: theme.colors.text.primary,
    },
}))

export const StyledDoseRow = styled('div')<StyledDosedRowProps>(({ type }) => ({
    display: 'flex',
    justifyContent: 'space-between',
    [breakpoint('md')]: {
        maxWidth: type === 'large' ? '50%' : 'unset',
    },

    ['h6']: {
        margin: 0,
        fontSize: type === 'large' ? calcRem(15) : calcRem(12),
        fontWeight: type === 'large' ? 700 : 'normal',
    },
}))

export const StyledLink = styled('p')({
    fontSize: calcRem(12),
    margin: 0,
    ['a']: {
        textDecoration: 'none',
        color: blue,
    },
})

export const StyledScheduleLink = styled('button')<{ isOpen?: boolean }>(
    ({ isOpen }) => ({
        border: 'none',
        padding: 0,
        margin: 0,
        color: isOpen ? black : blue,
        height: calcRem(15),
        textDecoration: 'none',
        cursor: 'pointer',
        background: 'none',
        pointerEvents: isOpen ? 'none' : 'unset',
        fontSize: calcRem(12),
        [':hover']: {
            color: black,
        },
    }),
)

export const StyledDoseItem = styled('div')({
    display: 'flex',
})
export const StyledFlexLinkRow = styled('div')({
    display: 'flex',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: calcRem(8),
    flexDirection: 'row',
    alignItems: 'flex-start',
})

export const DotIcon = styled('div')<DotIconProps>(({ color }) => ({
    height: calcRem(16),
    width: calcRem(16),
    borderRadius: '50%',
    border: '1px solid',
    padding: '2px',
    marginRight: calcRem(8),
    position: 'relative',
    top: '5px',
    ['span']: {
        backgroundColor:
            color === 'blue' ? blue : color === 'black' ? black : '#fff',
        display: 'block',
        height: '100%',
        width: '100%',
        borderRadius: '50%',
    },
}))
